import type { SupabaseClient } from '@supabase/supabase-js';

export type EventEntryCore = {
    id?: string;
    name: string;
    whoContactId?: string;
    whoName?: string;
    whoAvatar?: string | null;
    participants?: Array<{ contactId?: string; name: string; avatar?: string | null }>;
    where?: string;
    whereMeta?: any;
    when?: string; // ISO
    notes?: string;
    entries?: Array<{ when: string; note: string }>;
    customFields?: Record<string, any>;
    photoUrl?: string | null;
    tasks?: Array<{ id: string; text: string; done: boolean; dueDate?: string | null; recurrence?: '' | 'daily' | 'weekly' | 'monthly'; todoId?: string | null }>;
};

export type EventRowCore = {
    id: string;
    contact_id: string;
    name: string;
    who_contact_id: string | null;
    who_name: string | null;
    who_avatar: string | null;
    where_text: string | null;
    where_meta: any;
    when_date: string | null;
    notes: string | null;
    photo_url: string | null;
    tasks: any[] | null;
    participants?: any[] | null;
    entries?: any[] | null;
    custom_fields?: Record<string, string> | null;
    created_at?: string;
    updated_at?: string;
};

function toEntry(r: EventRowCore): EventEntryCore {
    return {
        id: r.id,
        name: r.name,
        whoContactId: r.who_contact_id || undefined,
        whoName: r.who_name || undefined,
        whoAvatar: r.who_avatar || undefined,
        participants: (r as any).participants || undefined,
        where: r.where_text || undefined,
        whereMeta: r.where_meta || undefined,
        when: r.when_date || undefined,
        notes: r.notes || undefined,
        entries: (r as any).entries || undefined,
        customFields: (r as any).custom_fields || undefined,
        photoUrl: r.photo_url || undefined,
        tasks: (r.tasks as any[]) || [],
    };
}

function fromEntry(contactId: string, e: EventEntryCore) {
    return {
        contact_id: contactId,
        name: e.name,
        who_contact_id: e.whoContactId ?? null,
        who_name: e.whoName ?? null,
        who_avatar: e.whoAvatar ?? null,
        participants: Array.isArray(e.participants) ? e.participants : [],
        where_text: e.where ?? null,
        where_meta: e.whereMeta ?? {},
        when_date: e.when ?? null,
        notes: e.notes ?? null,
        entries: Array.isArray(e.entries) ? e.entries : [],
        custom_fields: e.customFields ?? {},
        photo_url: e.photoUrl ?? null,
        tasks: Array.isArray(e.tasks) ? e.tasks : [],
    };
}

function isMissingEventsTable(err: any) {
    const msg = String(err?.message || err?.details || err);
    return /relation .*contact_events.* does not exist/i.test(msg) || /table .*contact_events.* does not exist/i.test(msg);
}

export async function listEventsCore(supabase: SupabaseClient, contactId: string): Promise<EventEntryCore[]> {
    const { data, error } = await supabase
        .from('contact_events')
        .select('*')
        .eq('contact_id', contactId)
        .order('when_date', { ascending: false })
        .order('created_at', { ascending: false });
    if (error) {
        if (isMissingEventsTable(error)) {
            throw new Error('Events feature not initialized. Apply migration 20250814_create_contact_events.sql to your Supabase project.');
        }
        throw error;
    }
    return (data as EventRowCore[] | null)?.map(toEntry) || [];
}

export async function createEventCore(supabase: SupabaseClient, contactId: string, entry: EventEntryCore): Promise<EventEntryCore> {
    const normalized: EventEntryCore = { ...entry };
    
    // Debug logging for name field issues
    console.log('🔍 createEventCore input:', { contactId, entryName: entry.name, normalizedName: normalized.name });
    
    // Ensure name is never null or empty (required field)
    if (!normalized.name || normalized.name.trim() === '') {
        console.warn('⚠️ Event name was null/empty, setting to fallback:', normalized.name);
        normalized.name = 'Untitled Event';
    }
    
    if ((entry.entries && entry.entries.length > 0) && (!entry.when || !entry.notes)) {
        const first = entry.entries[0];
        normalized.when = normalized.when || first.when;
        normalized.notes = normalized.notes || first.note;
    }
    const payload = fromEntry(contactId, normalized);
    
    // Debug the final payload being sent to database
    console.log('🔍 Final payload to database:', { name: payload.name, contactId: payload.contact_id });
    const { data, error } = await supabase
        .from('contact_events')
        .insert(payload)
        .select('*')
        .single();
    if (error) {
        if (isMissingEventsTable(error)) {
            throw new Error('Events feature not initialized. Apply migration 20250814_create_contact_events.sql to your Supabase project.');
        }
        throw error;
    }
    return toEntry(data as EventRowCore);
}

export async function updateEventCore(supabase: SupabaseClient, id: string, entry: EventEntryCore): Promise<EventEntryCore> {
    const { data, error } = await supabase
        .from('contact_events')
        .update({
            name: entry.name,
            who_contact_id: entry.whoContactId ?? null,
            who_name: entry.whoName ?? null,
            who_avatar: entry.whoAvatar ?? null,
            participants: Array.isArray(entry.participants) ? entry.participants : [],
            where_text: entry.where ?? null,
            where_meta: entry.whereMeta ?? {},
            when_date: entry.when ?? null,
            notes: entry.notes ?? null,
            photo_url: entry.photoUrl ?? null,
            tasks: Array.isArray(entry.tasks) ? entry.tasks : [],
            updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .select('*')
        .single();
    if (error) {
        if (isMissingEventsTable(error)) {
            throw new Error('Events feature not initialized. Apply migration 20250814_create_contact_events.sql to your Supabase project.');
        }
        throw error;
    }
    return toEntry(data as EventRowCore);
}

export async function deleteEventCore(supabase: SupabaseClient, id: string): Promise<void> {
    const { error } = await supabase
        .from('contact_events')
        .delete()
        .eq('id', id);
    if (error) {
        if (isMissingEventsTable(error)) {
            throw new Error('Events feature not initialized. Apply migration 20250814_create_contact_events.sql to your Supabase project.');
        }
        throw error;
    }
}

export async function getEventOwnerContactIdCore(supabase: SupabaseClient, eventId: string): Promise<string | null> {
    if (!eventId) throw new Error('getEventOwnerContactIdCore: eventId is required');
    const { data, error } = await supabase
        .from('contact_events')
        .select('contact_id')
        .eq('id', eventId)
        .maybeSingle();
    if (error) {
        if (isMissingEventsTable(error)) {
            throw new Error('Events feature not initialized. Apply migration 20250814_create_contact_events.sql to your Supabase project.');
        }
        throw error;
    }
    return ((data as any)?.contact_id as string) || null;
}

// List all events for contacts owned by a specific user. Useful for backfills and dashboards.
export async function listEventsForUserCore(
    supabase: SupabaseClient,
    userId: string,
    opts?: { withDateOnly?: boolean }
): Promise<EventEntryCore[]> {
    if (!userId) throw new Error('listEventsForUserCore: userId is required');
    let q = supabase
        .from('contact_events')
        // Disambiguate which contacts relationship to join: use the owner contact via contact_id
        .select('*, contacts!contact_events_contact_id_fkey(user_id)')
        // Filter using the embedded alias path (without relationship specifier)
        .eq('contacts.user_id', userId)
        .order('when_date', { ascending: false })
        .order('created_at', { ascending: false });
    if (opts?.withDateOnly) {
        q = q.not('when_date', 'is', null);
    }
    const { data, error } = await q;
    if (error) {
        if (isMissingEventsTable(error)) {
            throw new Error('Events feature not initialized. Apply migration 20250814_create_contact_events.sql to your Supabase project.');
        }
        throw error;
    }
    // Strip the join alias and map
    return ((data || []) as any[]).map((row) => {
        const { contacts: _c, ...rest } = row || {};
        return toEntry(rest as EventRowCore);
    });
}
